import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock } = FiIcons;

const Timer = ({ currentPlayer, gameStatus, onTimeUp }) => {
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    setTimeLeft(120);
    setIsActive(gameStatus === 'playing' || gameStatus === 'check');
  }, [currentPlayer, gameStatus]);

  useEffect(() => {
    let interval = null;
    
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            onTimeUp();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isActive, timeLeft, onTimeUp]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeLeft <= 30) return 'text-red-400 border-red-400';
    if (timeLeft <= 60) return 'text-yellow-400 border-yellow-400';
    return 'text-green-400 border-green-400';
  };

  const getProgressPercentage = () => {
    return (timeLeft / 120) * 100;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 min-w-[200px]"
    >
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <SafeIcon icon={FiClock} className="text-white text-xl" />
          <span className="text-white font-semibold">
            {currentPlayer === 'white' ? 'White' : 'Black'}'s Turn
          </span>
        </div>
        
        <div className="relative">
          <motion.div
            className={`text-4xl font-mono font-bold border-2 rounded-lg p-4 ${getTimerColor()}`}
            animate={{ 
              scale: timeLeft <= 10 ? [1, 1.05, 1] : 1,
            }}
            transition={{ 
              duration: 1, 
              repeat: timeLeft <= 10 ? Infinity : 0 
            }}
          >
            {formatTime(timeLeft)}
          </motion.div>
          
          {/* Progress bar */}
          <div className="mt-3 bg-gray-700 rounded-full h-2 overflow-hidden">
            <motion.div
              className={`h-full transition-colors duration-300 ${
                timeLeft <= 30 ? 'bg-red-400' : 
                timeLeft <= 60 ? 'bg-yellow-400' : 'bg-green-400'
              }`}
              initial={{ width: '100%' }}
              animate={{ width: `${getProgressPercentage()}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
        
        {timeLeft <= 30 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-300 text-sm mt-2 font-semibold"
          >
            Time running out!
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default Timer;